import { AxiosError } from 'axios';
import jwtDecode from 'jwt-decode';
import { matchPath } from 'react-router-dom';

import { eventEmitter } from 'config';
import { ROUTE_PATH } from 'constants/routes';
import {
  EventEmitterEvent,
  SessionExpiredReason,
  StatusCode,
  UserRole,
} from 'enums';

import { returnUrlUtils } from './return-url.utils';
import { storageUtils } from './storage.utils';

const refreshAccessTokenExpiresIn = 30 * 1000; // 30 seconds

const KEY = 'spay-auth';

const ERROR_MESSAGE = {
  ACCOUNT_DISABLED: 'Account disabled',
  INVALID_CLIENT_IP: 'Invalid client IP',
};

export type AuthData = {
  accessToken: string;
  refreshToken: string;
};

type TokenPayload = {
  exp: number;
  email: string;
  role: UserRole;
  twoFA: boolean;
};

export type ConnectionLostData = {
  email: string;
  previousPath?: string;
  accessToken: string;
} | null;

let authData: AuthData = null as any as AuthData;
let accessTokenExpiresIn: number | null = null;
let connectionLostData: ConnectionLostData = null;

const LOGOUT_ROUTES = [
  ROUTE_PATH.PUBLIC.LOGIN,
  ROUTE_PATH.PUBLIC.SESSION_EXPIRED,
  ROUTE_PATH.PUBLIC.ACCOUNT_DISABLED,
];

const isLogoutHandled = () =>
  LOGOUT_ROUTES.some((route) => !!matchPath(route, window.location.pathname));

const isAccountDisabledError = (error: AxiosError<{ message: string }>) =>
  error?.response?.status === StatusCode.Unauthorized &&
  error?.response?.data?.message === ERROR_MESSAGE.ACCOUNT_DISABLED;

const isInvalidClientIpError = (error: AxiosError<{ message: string }>) =>
  error?.response?.status === StatusCode.Unauthorized &&
  error?.response?.data?.message === ERROR_MESSAGE.INVALID_CLIENT_IP;

const setAccessTokenExpiresIn = () => {
  accessTokenExpiresIn = getTokenPayload()?.exp * 1000;
};

const setAuthData = (data: AuthData) => {
  authData = data;
  setAccessTokenExpiresIn();
  storageUtils.setItem(KEY, authData);
};

const resetAuthData = () => {
  authData = null as any as AuthData;
  accessTokenExpiresIn = null;
  storageUtils.removeItem(KEY);
};

const getAuthData = () => authData || storageUtils.getItem(KEY);

const getAccessToken = () => getAuthData()?.accessToken;

const isLoggedIn = () => !!getAuthData() && getTokenPayload()?.twoFA;

const getConnectionLostData = () => connectionLostData;

const clearConnectionLostData = () => {
  connectionLostData = null;
};

const logout = (error?: any) => {
  if (connectionLostData) {
    return;
  }

  const isAccountDisabled = isAccountDisabledError(error);
  const returnUrl = returnUrlUtils.getCurrentUrl();

  if (!isLoggedIn()) {
    resetAuthData();
    // an earlier logout call has already navigated the user away, keep its context
    if (!isLogoutHandled()) {
      eventEmitter.emit(EventEmitterEvent.Navigate, {
        to: ROUTE_PATH.PUBLIC.LOGIN,
      });
    }
    return;
  }

  const email = getTokenPayload()?.email;

  if (isAccountDisabled) {
    resetAuthData();
    const route = ROUTE_PATH.PUBLIC.ACCOUNT_DISABLED;
    if (!matchPath(route, window.location.pathname)) {
      eventEmitter.emit(EventEmitterEvent.Navigate, {
        to: route,
        options: { state: { email, returnUrl } },
      });
    }
    return;
  }

  const isNetworkError = !error?.response;

  if (isNetworkError) {
    // Connection lost: save data in memory for retry on SessionExpired page
    const accessToken = getAccessToken();
    connectionLostData = { email, previousPath: returnUrl, accessToken };
  }

  resetAuthData();

  const reason: SessionExpiredReason | undefined = isInvalidClientIpError(error)
    ? SessionExpiredReason.IpChanged
    : undefined;

  const route = ROUTE_PATH.PUBLIC.SESSION_EXPIRED;
  if (!matchPath(route, window.location.pathname)) {
    eventEmitter.emit(EventEmitterEvent.Navigate, {
      to: route,
      options: {
        state: { email, returnUrl, ...(!isNetworkError && { reason }) },
      },
    });
  }
};

const getTokenPayload = (accessToken?: string): TokenPayload => {
  const token = accessToken || getAccessToken();
  return (token ? jwtDecode(token) : null) as TokenPayload;
};

const getUserRole = () => getTokenPayload().role;
const getUserEmail = () => getTokenPayload()?.email;
const getTwoFAVerified = (accessToken?: string) =>
  getTokenPayload(accessToken)?.twoFA;

const isUserRole = (role: UserRole) => getUserRole() === role;

const getAccessTokenExpiresIn = () => {
  if (!accessTokenExpiresIn) {
    setAccessTokenExpiresIn();
  }
  return accessTokenExpiresIn;
};

const accessTokenExpiresSoon = () => {
  const expiresIn = getAccessTokenExpiresIn();
  return !!expiresIn && expiresIn - Date.now() < refreshAccessTokenExpiresIn;
};

export const authUtils = {
  isLoggedIn,
  getAuthData,
  getAccessToken,
  getUserRole,
  getUserEmail,
  setAuthData,
  resetAuthData,
  logout,
  isUserRole,
  getTwoFAVerified,
  accessTokenExpiresSoon,
  isAccountDisabledError,
  getConnectionLostData,
  clearConnectionLostData,
};
